// Which service categories can be booked in the logged-in app.
//
// Admins pause and resume services per country, with optional city overrides, in
// the admin console (App Settings > Service Availability; backend
// SERVICE_AVAILABILITY, GET /public/GetServiceAvailability). Components read them
// through useServiceAvailability() (utils/useServiceAvailability.js), which hands
// back the helpers below bound to the market's settings. The same settings make
// the chat-support AI answer questions about a paused service with its message.
//
// SERVICE_AVAILABILITY below is only the fallback: what applies while the
// settings load and if the API can't be reached. It matches the values seeded in
// the backend migration, so a failed fetch doesn't reopen a paused service.
//
// Options per category (keys are the requestType values):
//   showOnHome      - false hides the category's button on the /home screen
//                     (default true; the button also needs the category to exist
//                     in requestCategorySettings).
//   paused          - true: requests can still be submitted, but they cannot be
//                     confirmed. The cost card shows `pausedMessage` instead of
//                     Confirm Service.
//   pausedSubTypes  - pause only these sub-types, e.g. ['Plumbing'] for handyman
//                     or ['Babysitter'] for helper. The rest stay bookable. Handyman
//                     sub-types are the first booking question: "Electrical",
//                     "Plumbing", "Door, cupboard/cabinet".
//   allowAdvancePaymentWhenPaused - helper only: requests with an advance payment
//                     (the /booking/<id> flow) can still be confirmed while
//                     `paused` is true.
//   pausedMessage   - shown wherever a paused service would otherwise be booked.

const REVAMP_MESSAGE = (segment) =>
    `Our ${segment} services segment is undergoing a major revamp, which may take several weeks to be resumed. Apologies for any inconvenience caused.`

export const SERVICE_AVAILABILITY = {
    'Aircon Services': {
        showOnHome: true,
    },
    'Fitness Trainer': {
        showOnHome: true,
    },
    'Domestic Services': {
        showOnHome: true,
        // Standard helper bookings are paused; advance-payment bookings still work.
        paused: true,
        allowAdvancePaymentWhenPaused: true,
        // Blocked at the date/time step before a request is even created.
        pausedSubTypes: ['Elderly Care', 'Cooking Assistance', 'Babysitter'],
        pausedMessage: REVAMP_MESSAGE('helper'),
    },
    'Handyman Services': {
        showOnHome: true,
        paused: true,
        // Only Electrical is open. Values are the key-1 service types from
        // utils/handyman-quickbook.js.
        pausedSubTypes: ['Plumbing', 'Door, cupboard/cabinet', 'Drilling & Installation', 'Painting'],
        pausedMessage: REVAMP_MESSAGE('handyman'),
    },
}

// A handyman request's sub-type is the answer to the first booking question,
// stored as { 1: "Electrical", criteria: "Handyman service type - Electrical" }.
// The quick-book flows (HandymanBooking, HandymanDealItems,
// /home/services/handyman) and the question wizard (ServiceCriteria) both store
// it under key "1". The criteria text is the fallback.
export const getHandymanSubType = (request) => {
    for (const c of request?.criteria || []) {
        if (typeof c?.['1'] === 'string') return c['1']
        const match = /Handyman service type - (.+)$/i.exec(c?.criteria || '')
        if (match) return match[1].trim()
    }
    return null
}

/**
 * The helpers for one market's settings ({ [requestType]: options }, as above).
 * A request type with no entry is available and shown on home.
 */
export const availabilityHelpers = (availability = SERVICE_AVAILABILITY) => {
    const settingsFor = (requestType) => availability[requestType] || {}

    const isShownOnHome = (requestType) => settingsFor(requestType).showOnHome !== false

    const getPausedMessage = (requestType) =>
        settingsFor(requestType).pausedMessage || REVAMP_MESSAGE('service')

    const isSubTypePaused = (requestType, subType) =>
        (settingsFor(requestType).pausedSubTypes || []).includes(subType)

    /**
     * Whether a submitted request may be confirmed and paid.
     * @param {object} request  the request, including criteria and serviceCosts
     */
    const canConfirmRequest = (request) => {
        const settings = settingsFor(request?.requestType)

        if (settings.paused) {
            const hasAdvancePayment = request?.serviceCosts?.advancePayment > 0
            if (!(settings.allowAdvancePaymentWhenPaused && hasAdvancePayment)) return false
        }

        if (request?.requestType === 'Handyman Services') {
            const subType = getHandymanSubType(request)
            // Unknown sub-type: allow it only when nothing is paused.
            if (!subType) return !(settings.pausedSubTypes || []).length
            return !isSubTypePaused('Handyman Services', subType)
        }

        return true
    }

    return { isShownOnHome, getPausedMessage, isSubTypePaused, canConfirmRequest }
}
